import { Box, Typography, useTheme } from "@mui/material";
import "./MultiSectionVisualization.css";

interface MultiSectionVisualizationProps {
    /** Label to display in the center of the section */
    label?: string;
    /** Whether this is the leftmost section */
    leftEnding?: boolean;
    /** Whether this is the rightmost section */
    rightEnding?: boolean;
    /** Whether this section is active */
    isActive?: boolean;
    /** Click handler */
    onClick?: () => void;
    /** Section number */
    sectionNumber?: number;
    /** Scale factor for the section size */
    scaling?: number;
}

/**
 * Heat exchanger section visualization component
 * 
 * Displays a rectangular heat exchanger section with configurable borders and a centered label
 */
export const MultiSectionVisualization: React.FC<MultiSectionVisualizationProps> = ({
    label,
    leftEnding = false,
    rightEnding = false,
    isActive = false,
    onClick,
    scaling = 1,
}) => {
    const theme = useTheme();
    
    // Border colors for heat exchanger section
    const PRIMARY_BORDER_COLOR = "#1976d2"; // Changed to match Material UI primary color
    const SECONDARY_BORDER_COLOR = "#a4a4a4";
    
    // Styles for the component
    const styles = {
        container: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '130%',
            // height: '100%',
        },
        heatExchangerSection: {
            // Striped background pattern
            backgroundImage: 'linear-gradient(90deg, transparent 50%, #d9d7d7 50%)',
            backgroundSize: '5px 8px',
            backgroundColor: '#cbcbcb',
            
            height: 100,
            width: 220 * scaling,
            marginLeft: '9px',
            
            // Layout
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: "relative",
            
            // Borders - ensure blue borders are visible
            borderLeft: `8px solid ${leftEnding ? PRIMARY_BORDER_COLOR : SECONDARY_BORDER_COLOR}`,
            borderRight: `8px solid ${rightEnding ? PRIMARY_BORDER_COLOR : SECONDARY_BORDER_COLOR}`,
            
            // Border radius
            borderTopLeftRadius: leftEnding ? 5 : 0,
            borderBottomLeftRadius: leftEnding ? 5 : 0,
            borderTopRightRadius: rightEnding ? 5 : 0,
            borderBottomRightRadius: rightEnding ? 5 : 0,
        },
        labelContainer: {
            // Layout
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            
            // Appearance
            backgroundColor: isActive ? theme.palette.primary.main : 'white',
            borderRadius: '50%',
            width: 28, // Reduced size
            height: 28, // Reduced size
            zIndex: 3,
        },
        label: {
            color: isActive ? 'white' : 'black',
            fontWeight: 600,
            fontSize: '0.875rem',
        },
    };

    return (
        <Box sx={styles.container}>
            <Box 
                sx={styles.heatExchangerSection}
                onClick={onClick}
            >
                <Box sx={styles.labelContainer}>
                    <Typography sx={styles.label}>
                        {label}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}; 